import React, { useEffect, useState, useMemo } from "react";
import { Modal, ModalBody, ModalContent, ModalHeader } from "components/modal";
import ButtonNew from "components/buttonNew/buttonNew";
import { Checkmark, Close } from "assets/icons";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { Input, Select, SelectItem, Divider } from "@heroui/react";
import { API_URL } from "constants/index";
import { LockKey } from "@phosphor-icons/react";

interface AddAccessControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface OwnedBuilding {
  id: string;
  company?: { name: string };
}

interface OwnedUnit {
  id: string;
  buildingId: string;
  unitType?: string;
}

const ACCESS_TYPES = ["Sleutel", "Analoog", "Digitaal", "Hangslot"];
const KEY_LOCATIONS = ["Bij gebouw", "Op kantoor", "Leverancier", "Makelaar"];

const AddAccessControlModal: React.FC<AddAccessControlModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const { session } = useSession();

  const [buildings, setBuildings] = useState<OwnedBuilding[]>([]);
  const [units, setUnits] = useState<OwnedUnit[]>([]);
  const [buildingId, setBuildingId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [accessType, setAccessType] = useState("");
  const [keyLocation, setKeyLocation] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchOwnedBuildings();
    }
  }, [isOpen]);

  const fetchOwnedBuildings = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/access-control/buildings`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      setBuildings(data.buildings);
      setUnits(data.units);
    } catch (error) {
      console.error("Failed to load owned buildings:", error);
      toast.error("Fout bij het laden van gebouwen.");
    }
  };

  const filteredUnits = useMemo(() => {
    if (!buildingId) return [];
    return units.filter((unit) => unit.buildingId === buildingId);
  }, [buildingId, units]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const resetForm = () => {
    setBuildingId("");
    setUnitId("");
    setAccessType("");
    setKeyLocation("");
    setAdditionalInfo("");
  };

  const handleClose = () => {
    if (isLoading) return;
    resetForm();
    onClose();
  };

  const handleCreate = async () => {
    if (!buildingId) {
      return toast.error("Selecteer een gebouw");
    }
    if (!accessType) {
      return toast.error("Selecteer een type toegang");
    }

    setIsLoading(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/access-control`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            buildingId,
            unitId: unitId || null,
            accessType,
            keyLocation: keyLocation || null,
            additionalInfo: additionalInfo || null,
          }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      toast.success("Toegangscontrole succesvol aangemaakt!");
      resetForm();
      onCreated();
      onClose();
    } catch (error) {
      console.error("Error creating access control:", error);
      toast.error("Fout bij het aanmaken van toegangscontrole");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal size="2xl" hideCloseButton className="z-100" isOpen={isOpen}>
      <ModalContent className="max-h-[90vh] overflow-hidden">
        <ModalHeader className="flex flex-col gap-1 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-moss-primary/10 p-2">
              <LockKey className="h-5 w-5 text-moss-primary" weight="duotone" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-primary">
                Nieuwe toegangscontrole
              </h2>
              <p className="text-sm text-gray-500">
                Vul de gegevens in om een nieuwe toegangscontrole aan te maken
              </p>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="flex flex-col gap-4 overflow-y-auto px-6 py-6">
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Gebouw"
              placeholder="Selecteer gebouw"
              selectedKeys={buildingId ? [buildingId] : []}
              onChange={(e) => {
                setBuildingId(e.target.value);
                setUnitId("");
              }}
              isDisabled={isLoading}
              isRequired
              variant="bordered"
              classNames={{
                label: "text-sm font-medium",
                value: "text-sm",
              }}
            >
              {buildings.map((building) => (
                <SelectItem key={building.id}>
                  {building.id}
                  {building.company?.name ? ` - ${building.company.name}` : ""}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="Unit"
              placeholder={
                !buildingId
                  ? "Selecteer eerst een gebouw"
                  : filteredUnits.length === 0
                    ? "Geen units beschikbaar"
                    : "Selecteer unit (optioneel)"
              }
              selectedKeys={unitId ? [unitId] : []}
              onChange={(e) => setUnitId(e.target.value)}
              isDisabled={isLoading || !buildingId || filteredUnits.length === 0}
              variant="bordered"
              classNames={{
                label: "text-sm font-medium",
                value: "text-sm",
              }}
            >
              {filteredUnits.map((unit) => (
                <SelectItem key={unit.id}>
                  {unit.id}
                  {unit.unitType ? ` - ${unit.unitType}` : ""}
                </SelectItem>
              ))}
            </Select>

            <Select
              label="Type Toegang"
              placeholder="Selecteer type"
              selectedKeys={accessType ? [accessType] : []}
              onChange={(e) => setAccessType(e.target.value)}
              isDisabled={isLoading}
              isRequired
              variant="bordered"
              classNames={{
                label: "text-sm font-medium",
                value: "text-sm",
              }}
            >
              {ACCESS_TYPES.map((type) => (
                <SelectItem key={type}>{type}</SelectItem>
              ))}
            </Select>

            <Select
              label="Locatie Sleutel"
              placeholder="Selecteer locatie (optioneel)"
              selectedKeys={keyLocation ? [keyLocation] : []}
              onChange={(e) => setKeyLocation(e.target.value)}
              isDisabled={isLoading}
              variant="bordered"
              classNames={{
                label: "text-sm font-medium",
                value: "text-sm",
              }}
            >
              {KEY_LOCATIONS.map((loc) => (
                <SelectItem key={loc}>{loc}</SelectItem>
              ))}
            </Select>

            <Input
              label="Additionele informatie"
              placeholder="Extra informatie..."
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              disabled={isLoading}
              variant="bordered"
              classNames={{
                label: "text-sm font-medium",
                input: "text-sm",
              }}
              className="col-span-2"
            />
          </div>
        </ModalBody>

        <Divider />

        <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50">
          <ButtonNew
            variant="secondaryNeutralMid"
            onClick={handleClose}
            icon={<Close />}
            label="Annuleren"
            isDisabled={isLoading}
          />
          <ButtonNew
            variant="primaryMoss"
            onClick={handleCreate}
            icon={<Checkmark />}
            label="Toevoegen"
            isDisabled={isLoading || !buildingId || !accessType}
          />
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AddAccessControlModal;
